import type { SupabaseClient } from "@supabase/supabase-js";

import { isMissingFeedSurface } from "../../../../packages/shared/src/feed.ts";
import {
  APPROVE_OPEN_SHIFT_REQUEST_RPC,
  LIST_ACCOUNT_OPEN_SHIFT_REQUESTS_RPC,
  OPEN_SHIFT_REQUEST_TEXT_MAX,
  REJECT_OPEN_SHIFT_REQUEST_RPC,
  SCHEDULE_MANAGE_PERMISSION,
  asColorToken,
  asOpenFillMode,
  asOpenShiftRequestStatus,
  type ColorToken,
  type OpenFillMode,
  type OpenShiftRequestStatus,
} from "../../../../packages/shared/src/schedule-color.ts";

// Desk-side open-shift request inbox (Wave 6A migration 20260808120000). The console owns
// only the three `schedule.manage` paths below; request / cancel / list-mine are the guard
// lane's (6C, 6D) and are deliberately not reachable from here.
//
// Rows live in `app_private.open_shift_requests` with NO table privileges for
// `authenticated`: every read and write goes through a public SECURITY INVOKER wrapper over
// a caller-bound private implementation. The server re-checks membership and `schedule.manage`
// on every call, so the `canManage` prop below is presentation, never the gate.

export {
  APPROVE_OPEN_SHIFT_REQUEST_RPC,
  LIST_ACCOUNT_OPEN_SHIFT_REQUESTS_RPC,
  REJECT_OPEN_SHIFT_REQUEST_RPC,
};

export const OPEN_SHIFT_REQUESTS_MANAGE_PERMISSION = SCHEDULE_MANAGE_PERMISSION;

/** One page of pending work. The contract exposes no count RPC - the badge is this length. */
export const OPEN_SHIFT_REQUESTS_PAGE_LIMIT = 50;

/** The 6A contract's hard cap. The server refuses anything longer. */
export const REJECTION_REASON_MAX = OPEN_SHIFT_REQUEST_TEXT_MAX;

/**
 * The console's own, stricter cap. Editorial, not contractual: a decline reason is read by
 * one officer on a phone, and 500 characters of it is a wall, not a message. 280 < 500, so
 * this needs no contract change and the server sentence still governs anything longer.
 */
export const REJECTION_REASON_CONSOLE_MAX = 280;

export const DECLINE_REASON_REQUIRED =
  "Write a reason before declining. The officer sees exactly what you write.";
/**
 * What the desk is told after a decline. Both clauses are checked, not assumed: the reason is
 * saved on the request row, and `reject_open_shift_request` flips that row without touching
 * the slot, so the seat genuinely stays open. It deliberately does NOT say the officer has
 * seen it, because today no guard client renders a declined request.
 */
export const DECLINE_RECEIPT =
  "Declined. The reason is saved on the request and the seat stays open.";

export const DECLINE_REASON_TOO_LONG = `Reason must be ${REJECTION_REASON_CONSOLE_MAX} characters or fewer.`;

/**
 * Bidi overrides, embeddings and isolates. They reorder the characters around them, so a
 * reason can be stored as one thing and rendered in the Guard app as another (the Trojan
 * Source class, CVE-2021-42574). A one-sentence operational message never needs them, and
 * removing them does not affect ordinary Arabic or Hebrew text - the Unicode bidi algorithm
 * orders that correctly on its own.
 */
const BIDI_CONTROLS = /[\u202A-\u202E\u2066-\u2069]/g;

/**
 * Everything that occupies no visual space, expressed as a Unicode PROPERTY rather than a
 * hand-written list. `String.prototype.trim()` removes none of it, so without this a reason
 * of one zero-width space satisfies "required" and still reaches the officer as a blank
 * decline.
 *
 * An enumerated list was the first attempt and it leaked: U+200C, U+200D, U+2800, U+3164,
 * U+115F, U+17B4 and U+FFA0 all walked straight through it. Default_Ignorable_Code_Point is
 * the class Unicode maintains for exactly this question, so it covers the invisible LETTERS
 * (Hangul fillers) that a "format characters" list never will. Braille blank is added by
 * hand because it is a visible symbol that happens to render as nothing.
 *
 * Used ONLY to test legibility, never to rewrite the supervisor's words. That distinction is
 * what lets this be aggressive: U+200C and U+200D carry meaning in Persian, Indic scripts and
 * emoji, and `cleanDeclineReason` below preserves them in what actually gets stored.
 */
const INVISIBLE_CHARACTERS = /[\p{Default_Ignorable_Code_Point}\s\u2800]/gu;

/** The reason as it will be stored: the supervisor's words, minus text-reordering controls. */
export function cleanDeclineReason(reason: unknown): string {
  return typeof reason === "string" ? reason.replace(BIDI_CONTROLS, "").trim() : "";
}

/**
 * True only when the reason contains something an officer can actually see. This is the one
 * definition of "has a reason" - the surface disables its primary control on it and the write
 * below refuses on it, so the button state and the guard can never disagree.
 */
export function hasLegibleDeclineReason(reason: unknown): boolean {
  return cleanDeclineReason(reason).replace(INVISIBLE_CHARACTERS, "").trim().length > 0;
}

// "Nothing is pending" and "this server has no request workflow" are different facts, and
// only one of them means nobody is waiting on a supervisor.
export const OPEN_SHIFT_REQUESTS_UNAVAILABLE =
  "Open shift requests are not available on this server yet. Nothing is pending.";
export const OPEN_SHIFT_REQUESTS_LOAD_FAILED =
  "Could not load open shift requests. Check your connection and try again.";
export const OPEN_SHIFT_REQUESTS_EMPTY = "No open shift requests are waiting.";

export const SAFE_OPEN_SHIFT_REQUEST_FAILURE =
  "Couldn't complete that open shift request action - please try again.";

/** Verbatim from the shipped migration. Anything else collapses to the safe sentence. */
export const KNOWN_OPEN_SHIFT_REQUEST_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to manage open shift requests.",
  "Active account membership is required.",
  "Account id is required.",
  "Open shift request was not found or is not available.",
  "Open shift request is not pending.",
  "Open shift has no remaining seats.",
  "Open shift rejection reason must be 500 characters or fewer.",
  "Schedule management permission is required to review open shift requests.",
  // Raised by approve when the template's mode was flipped to instant claim under a
  // request that is still pending. Permanent, not a race: the seat can no longer be
  // approved, only rejected. Refreshing would not clear it.
  "Open shift for this template is claim-only. Use claim instead of request.",
]);

/** Refusals that mean the loaded list is stale, so the surface refetches after showing them. */
export const STALE_LIST_FAILURES: ReadonlySet<string> = new Set([
  "Open shift request was not found or is not available.",
  "Open shift request is not pending.",
  "Open shift has no remaining seats.",
]);

/** The 6A request row shape, identical across list / approve / reject. */
export interface OpenShiftRequest {
  readonly requestId: string;
  readonly shiftSlotId: string | null;
  readonly shiftTemplateId: string | null;
  readonly colorToken: ColorToken;
  readonly openFillMode: OpenFillMode;
  readonly siteName: string | null;
  readonly routeName: string | null;
  readonly slotStartsAt: string | null;
  readonly slotEndsAt: string | null;
  readonly status: OpenShiftRequestStatus;
  readonly requestNote: string | null;
  readonly requesterDisplayName: string | null;
  readonly rejectionReason: string | null;
  readonly routeAssignmentId: string | null;
  readonly createdAt: string | null;
  readonly reviewedAt: string | null;
}

export type OpenShiftRequestsLoad =
  | { readonly status: "ready"; readonly requests: readonly OpenShiftRequest[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

export type OpenShiftRequestOutcome =
  | { readonly ok: true; readonly request: OpenShiftRequest | null }
  | { readonly ok: false; readonly reason: string; readonly stale: boolean };

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

/** A row without an id or a recognized status is dropped, not guessed. */
export function mapOpenShiftRequest(raw: unknown): OpenShiftRequest | null {
  if (raw === null || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const requestId = asText(row.request_id);
  const status = asOpenShiftRequestStatus(row.status);
  if (requestId === null || status === null) return null;
  return {
    requestId,
    shiftSlotId: asText(row.shift_slot_id),
    shiftTemplateId: asText(row.shift_template_id),
    colorToken: asColorToken(row.color_token),
    openFillMode: asOpenFillMode(row.open_fill_mode),
    // The contract returns '' only when a name is truly unreachable; treat it as unknown
    // so the row renders the route alone rather than a gap.
    siteName: asText(row.site_name),
    routeName: asText(row.route_name),
    slotStartsAt: asText(row.slot_starts_at),
    slotEndsAt: asText(row.slot_ends_at),
    status,
    requestNote: asText(row.request_note),
    requesterDisplayName: asText(row.requester_display_name),
    rejectionReason: asText(row.rejection_reason),
    routeAssignmentId: asText(row.route_assignment_id),
    createdAt: asText(row.created_at),
    reviewedAt: asText(row.reviewed_at),
  };
}

export function mapOpenShiftRequests(data: unknown): OpenShiftRequest[] {
  const rows = Array.isArray(data) ? data : [];
  const mapped: OpenShiftRequest[] = [];
  for (const raw of rows) {
    const request = mapOpenShiftRequest(raw);
    if (request !== null) mapped.push(request);
  }
  return mapped;
}

/**
 * Pending requests for the account. Never throws: a missing RPC resolves to `unavailable`
 * so the surface can say the workflow is absent instead of implying an empty queue.
 */
export async function fetchAccountOpenShiftRequests(
  client: SupabaseClient,
  accountId: string,
  status: OpenShiftRequestStatus | null = "pending",
  limit: number = OPEN_SHIFT_REQUESTS_PAGE_LIMIT,
): Promise<OpenShiftRequestsLoad> {
  try {
    const { data, error } = await client.rpc(LIST_ACCOUNT_OPEN_SHIFT_REQUESTS_RPC, {
      p_account_id: accountId,
      p_limit: limit,
      p_status: status,
      p_shift_slot_id: null,
    });
    if (error === null) {
      return { status: "ready", requests: mapOpenShiftRequests(data) };
    }
    if (isMissingFeedSurface(error)) {
      return { status: "unavailable", reason: OPEN_SHIFT_REQUESTS_UNAVAILABLE };
    }
    // The list path raises allowlisted sentences too - most importantly the permission
    // refusal, which every user without schedule.manage hits on every poll. Telling them
    // to "check your connection" would be a lie about a working network.
    const message = typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
    return {
      status: "failed",
      reason: KNOWN_OPEN_SHIFT_REQUEST_FAILURES.has(message)
        ? message
        : OPEN_SHIFT_REQUESTS_LOAD_FAILED,
    };
  } catch {
    return { status: "failed", reason: OPEN_SHIFT_REQUESTS_LOAD_FAILED };
  }
}

async function runRequestWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<OpenShiftRequestOutcome> {
  try {
    const { data, error } = await client.rpc(fn, params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      const known = KNOWN_OPEN_SHIFT_REQUEST_FAILURES.has(message);
      return {
        ok: false,
        reason: known ? message : SAFE_OPEN_SHIFT_REQUEST_FAILURE,
        stale: known && STALE_LIST_FAILURES.has(message),
      };
    }
    const row = Array.isArray(data) ? data[0] : data;
    return { ok: true, request: mapOpenShiftRequest(row) };
  } catch {
    return { ok: false, reason: SAFE_OPEN_SHIFT_REQUEST_FAILURE, stale: false };
  }
}

/** Approve: the server creates the assignment through the same fill rules as instant claim. */
export async function approveOpenShiftRequest(
  client: SupabaseClient,
  accountId: string,
  requestId: string,
): Promise<OpenShiftRequestOutcome> {
  return runRequestWrite(client, APPROVE_OPEN_SHIFT_REQUEST_RPC, {
    p_account_id: accountId,
    p_request_id: requestId,
  });
}

/**
 * Decline a request. The reason is REQUIRED, and `string` rather than `string | null` so
 * "declined with no explanation" is not expressible here at all.
 *
 * Wave 6B shipped this reason as optional, which made the field the desk could skip and the
 * officer could not: a guard whose request vanished with no words learns nothing and asks
 * again for the same seat. The server still accepts a null reason, so this is a console
 * policy, enforced at the only layer that has the supervisor in front of it.
 *
 * Both length rules run before the RPC. Neither is the security boundary (the table's check
 * constraint is); they spare the supervisor a round trip and keep the message readable.
 *
 * "Required" is measured on what an officer can SEE, not on what `trim()` happens to remove.
 * A reason of one zero-width space is a blank decline wearing a character, and the server's
 * own `nullif(btrim(...), '')` does not catch it either, so it is refused here.
 */
export async function rejectOpenShiftRequest(
  client: SupabaseClient,
  accountId: string,
  requestId: string,
  reason: string,
): Promise<OpenShiftRequestOutcome> {
  const cleaned = cleanDeclineReason(reason);
  if (!hasLegibleDeclineReason(cleaned)) {
    return { ok: false, reason: DECLINE_REASON_REQUIRED, stale: false };
  }
  if (cleaned.length > REJECTION_REASON_CONSOLE_MAX) {
    return { ok: false, reason: DECLINE_REASON_TOO_LONG, stale: false };
  }
  return runRequestWrite(client, REJECT_OPEN_SHIFT_REQUEST_RPC, {
    p_account_id: accountId,
    p_request_id: requestId,
    p_rejection_reason: cleaned,
  });
}
